import { Button, EButtonColorVariant, EModalVariants, Modal } from '@outdoorsyco/bonfire';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { ESearchSheetContent } from '@/constants/search';
import { ILocationSearchAutocompleteListOption } from '@/hooks/useLocationSearchAutocompleteOptions';
import { ILodgingCampgrounds } from '@/services/types/search/lodgingCampgrounds';
import { IVehicle } from '@/services/types/search/vehicle';
import { IVehicleCampgrounds } from '@/services/types/search/vehicleCampgrounds';
import { formatDateFullRange } from '@/utility/format-date';

import { MobileSearchAddressModalContent } from '../../NavbarSearch/MobileNavbarSearch/MobileSearchAddressModalContent';
import { MobileSearchDatesModalContent } from '../../NavbarSearch/MobileNavbarSearch/MobileSearchDatesModalContent';
import { MobileSearchGuestsModalContent } from '../../NavbarSearch/MobileNavbarSearch/MobileSearchGuestsModalContent';
import { INavbarSearch } from '../../SearchHeader';
import { EHeaderTab, IHeaderMenu } from '../UniversalSearchContainer/menu';
import FilterTriggerButton from './FilterTriggerButton';
import css from './MobileUniversalSearch.module.css';
import MobileUniversalSearchTowingVehicles from './MobileUniversalSearchTowingVehicles';
import MobileUniversalSearchVehiclesCampgrounds from './MobileUniversalSearchVehiclesCampgrounds';

interface IMobileUniversalSearchProps extends INavbarSearch {
  openedTab?: IHeaderMenu;
  onChangeVehicle?: (vehicle?: IVehicle) => void;
  onChangeVehicleCampgrounds?: (vehicle?: IVehicleCampgrounds) => void;
  onChangeLodgingCampgrounds?: (lodging?: ILodgingCampgrounds) => void;
  vehicle?: IVehicle;
  vehicleCampgrounds?: IVehicleCampgrounds;
  lodgingCampgrounds?: ILodgingCampgrounds;
  popularDestinations?: ILocationSearchAutocompleteListOption[];
  isStayFilterEnabled?: boolean;
  isGuestOccupancyAvailable?: boolean;
  isCampground?: boolean;
}

const MobileUniversalSearch: React.FC<IMobileUniversalSearchProps> = ({
  handleRecentSearchNavigation,
  location,
  address,
  dates,
  guests,
  recentSearches,
  onSubmit,
  onChangeAddress,
  onChangeDates,
  onChangeGuests,
  loading,
  openedTab,
  onChangeVehicle,
  onChangeVehicleCampgrounds,
  vehicle,
  vehicleCampgrounds,
  popularDestinations = [],
  isStayFilterEnabled,
  isGuestOccupancyAvailable,
  isCampground,
}) => {
  const intl = useIntl();
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [filterContent, setFilterContent] = useState<ESearchSheetContent>();

  const totalGuests =
    (guests?.adults || 0) +
    (guests?.children || 0) +
    (isGuestOccupancyAvailable ? guests?.infants || 0 : 0);

  const handleSubmit = () => {
    onSubmit?.();
  };

  const handleExpandFilter = (newContent: ESearchSheetContent) => {
    setIsModalOpened(true);
    setFilterContent(newContent);
  };

  const hasNoDates = useMemo<boolean>(() => !dates?.from && !dates?.to, [dates]);

  const formattedDates = useMemo<string | null>(() => {
    if (!dates?.from || !dates?.to) {
      return null;
    }

    const datesLabel = formatDateFullRange(dates.from.toString(), dates.to.toString()) || '';

    if (dates.flexible_days) {
      return `${datesLabel} (±${dates.flexible_days})`;
    }

    return datesLabel;
  }, [dates]);

  const addDatesMessage = isStayFilterEnabled
    ? intl.formatMessage({
        defaultMessage: 'When?',
        id: 'E0lVlB',
        description: 'When will you go?',
      })
    : intl.formatMessage({ defaultMessage: 'When will you go?', id: 'NVOwAF' });

  const locationPlaceholder = intl.formatMessage({
    defaultMessage: 'Where to?',
    id: 'XTTbsP',
    description: 'Global Header > Placeholder text for header search field',
  });

  return (
    <div className={`w-full bg-white rounded-[12px]`}>
      <form>
        <div className={isStayFilterEnabled ? css.stayLocation : css.location}>
          <FilterTriggerButton
            filterKey={ESearchSheetContent.ADDRESS}
            label={intl.formatMessage({
              defaultMessage: 'Location',
              id: 'rvirM2',
            })}
            ariaExpanded={isModalOpened && filterContent === ESearchSheetContent.ADDRESS}
            ariaLabel={intl.formatMessage(
              {
                defaultMessage:
                  'Click to {hasValue, select, true {change the} other {add a}} location',
                id: 'QKQlaA',
                description: 'Global Header > Search',
              },
              {
                hasValue: !!address,
              },
            )}
            content={address || locationPlaceholder}
            onClick={() => handleExpandFilter(ESearchSheetContent.ADDRESS)}
            btnClassName="-mx-0.5 "
          />
        </div>
        {/* TODO: these two filters should be migrated to the modal variant */}
        {openedTab?.tab === EHeaderTab.TOWABLE && (
          <div className={isStayFilterEnabled ? css.stayTowable : css.towable}>
            <MobileUniversalSearchTowingVehicles
              onChangeVehicle={onChangeVehicle}
              vehicle={vehicle}
            />
          </div>
        )}
        {openedTab?.tab === EHeaderTab.RV_SITE && (
          <div className={isStayFilterEnabled ? css.stayTowable : css.towable}>
            <MobileUniversalSearchVehiclesCampgrounds
              onChangeVehicle={onChangeVehicleCampgrounds}
              vehicle={vehicleCampgrounds}
            />
          </div>
        )}
        {/* TODO: readd lodging campground if we end up supporting lodging type */}
        <div className={isStayFilterEnabled ? css.stayDatesWrapper : css.datesWrapper}>
          <div className={isStayFilterEnabled ? css.stayDates : css.dates}>
            <FilterTriggerButton
              filterKey={ESearchSheetContent.DATES}
              label={intl.formatMessage({
                defaultMessage: 'Dates',
                id: 'D6b0Au',
              })}
              ariaExpanded={isModalOpened && filterContent === ESearchSheetContent.DATES}
              ariaLabel={intl.formatMessage(
                {
                  defaultMessage:
                    'Click to {hasValue, select, true {change the} other {add}} dates',
                  id: 'FiIHmX',
                  description: 'Global Header > Search',
                },
                {
                  hasValue: !hasNoDates,
                },
              )}
              content={hasNoDates || !formattedDates ? addDatesMessage : formattedDates}
              onClick={() => handleExpandFilter(ESearchSheetContent.DATES)}
            />
          </div>
          <div className={isStayFilterEnabled ? css.stayGuests : css.guests}>
            <FilterTriggerButton
              filterKey={ESearchSheetContent.GUESTS}
              label={intl.formatMessage({
                defaultMessage: 'Guests',
                id: 'NIVTxd',
              })}
              ariaExpanded={isModalOpened && filterContent === ESearchSheetContent.GUESTS}
              ariaLabel={intl.formatMessage(
                {
                  defaultMessage:
                    'Click to {hasValue, select, true {change the} other {add}} guests',
                  id: 'PB9EPa',
                  description: 'Global Header > Search',
                },
                {
                  hasValue: !!totalGuests,
                },
              )}
              content={
                !totalGuests
                  ? intl.formatMessage({
                      defaultMessage: 'Who‘s going?',
                      id: 'Q1dkVt',
                    })
                  : intl.formatMessage(
                      {
                        defaultMessage: '{totalGuests, plural, one {# guest} other {# guests}}',
                        id: 'YstT2S',
                        description: 'Placeholder for header guests field',
                      },
                      { totalGuests },
                    )
              }
              onClick={() => handleExpandFilter(ESearchSheetContent.GUESTS)}
            />
          </div>
        </div>
        <div className={isStayFilterEnabled ? css.staySearch : css.search}>
          <Button
            type="button"
            variant={EButtonColorVariant.Primary}
            label={intl.formatMessage({
              defaultMessage: 'Search',
              id: '7oIqma',
              description: 'Search button label',
            })}
            loading={loading}
            fullWidth
            onClick={handleSubmit}
          />
        </div>
      </form>

      <Modal
        isOpen={isModalOpened}
        onClose={() => setIsModalOpened(false)}
        variant={EModalVariants.Bottom}
        className="z-[1410]">
        {filterContent === ESearchSheetContent.ADDRESS && (
          <MobileSearchAddressModalContent
            onCloseHandler={() => setIsModalOpened(false)}
            location={location}
            address={address}
            onChangeAddress={onChangeAddress}
            popularDestinations={popularDestinations}
            recentSearches={recentSearches}
            handleRecentSearchNavigation={handleRecentSearchNavigation}
          />
        )}
        {filterContent === ESearchSheetContent.DATES && (
          <MobileSearchDatesModalContent
            onCloseHandler={() => setIsModalOpened(false)}
            dates={dates}
            onChangeDates={onChangeDates}
          />
        )}
        {filterContent === ESearchSheetContent.GUESTS && (
          <MobileSearchGuestsModalContent
            onCloseHandler={() => setIsModalOpened(false)}
            guests={guests}
            onChangeGuests={onChangeGuests}
            isGuestOccupancyAvailable={isGuestOccupancyAvailable}
            isCampground={isCampground}
          />
        )}
      </Modal>
    </div>
  );
};

export default MobileUniversalSearch;
